import { createApi } from '@reduxjs/toolkit/query/react';
import translateClient from './TranslateService';
import { showCloudTokenModal } from '../utils/cloudTokenModal';
import { lookupApi } from './lookupApi';
import { USER_UUID, API_TOKEN } from '../utils/constants';

/**
 * Clave API Cloud del usuario, leída de la caché de lookupApi
 */
const selectCloudToken = (state) =>
  lookupApi.endpoints.getCloudKey.select(localStorage.getItem(USER_UUID))(state)?.data;

/** baseQuery propio: inyecta el cloud token del store en cada petición. */
const translateBaseQuery =
  () =>
  async ({ url, method = 'get', data, params }, api) => {
    const token = selectCloudToken(api.getState());

    try {
      const res = await translateClient({
        url,
        method,
        data,
        params,
        headers: {
          ...(token ? { 'X-QUIIOT-TOKEN': token } : {}),
          'X-CORE-TOKEN': localStorage.getItem(API_TOKEN),
        },
      });
      return { data: res.data };
    } catch (err) {
      const status = err.response?.status;

      const upstream = /Error:\s*(\d{3})/.exec(String(err.response?.data))?.[1];
      // 401 propio = falta la cabecera. upstream 401 = kiCloud rechaza la clave.
      if (status === 401 || upstream === '401') showCloudTokenModal();

      return {
        error: {
          status: status ?? 'FETCH_ERROR',
          upstream: upstream ? Number(upstream) : undefined,
          data: err.response?.data ?? err.message,
        },
      };
    }
  };

export const translateApi = createApi({
  reducerPath: 'translateApi',
  baseQuery: translateBaseQuery(),
  tagTypes: ['LibraryParams'],
  endpoints: (build) => ({
    getLibraries: build.query({
      query: () => ({ url: 'libraries' }),
    }),
    getTemplates: build.query({
      query: () => ({ url: 'templates' }),
    }),
    getLibrary: build.query({
      query: (uuid) => ({ url: `libraries/${uuid}` }),
    }),
    getLibraryParams: build.query({
      query: (uuid) => ({ url: `libraries/${uuid}/params` }),
      providesTags: (result, error, uuid) => [{ type: 'LibraryParams', id: uuid }],
    }),
    getContexts: build.query({
      query: () => ({ url: 'contexts' }),
    }),
    translateOne: build.mutation({
      query: (body) => ({ url: 'translate', method: 'post', data: body }),
    }),
    updateLibraryParams: build.mutation({
      query: ({ uuid, parameters }) => ({
        url: `libraries/${uuid}/params`,
        method: 'put',
        data: { parameters },
      }),
      invalidatesTags: (result, error, { uuid }) => [{ type: 'LibraryParams', id: uuid }],
    }),
  }),
});

export const {
  useGetLibrariesQuery,
  useGetTemplatesQuery,
  useGetLibraryQuery,
  useGetLibraryParamsQuery,
  useGetContextsQuery,
  useTranslateOneMutation,
  useUpdateLibraryParamsMutation,
} = translateApi;
